import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  User, 
  Building2, 
  Calendar,
  Menu, 
  X, 
  LogOut,
  Settings,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Building,
  Kanban,
  Lock,
  Briefcase,
  Compass,
  Globe,
  FlaskConical,
  Cpu,
  Target,
  Megaphone,
  Rocket,
  ClipboardList,
  Radio,
  UsersRound
} from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Reorganized navigation with sections
const navStructure = [
  { 
    type: "item",
    path: "/dashboard", 
    label: "Dashboard", 
    icon: LayoutDashboard, 
    visibilityKey: null 
  },
  { 
    type: "item",
    path: "/profile", 
    label: "Profile", 
    icon: User, 
    visibilityKey: null 
  },
  {
    type: "section",
    label: "Strategy",
    icon: Compass,
    visibilityKey: null, // Section header is always visible
    children: [
      { path: "/strategy/market",              label: "Market & Competitors", icon: Globe,        visibilityKey: "strategy_market_group" },
      { path: "/strategy/clinical?tab=clinical_proof",     label: "Clinical & Regulatory",             icon: FlaskConical, visibilityKey: "strategy_clinical_group" },
      { path: "/strategy/technology?tab=product",          label: "Technology, IP & Business Model",   icon: Cpu,          visibilityKey: "strategy_technology_group" },
      { path: "/strategy/milestones?tab=value_inflection", label: "Milestones, Team & Fundraising",    icon: Target,       visibilityKey: "strategy_milestones_group" },
      { path: "/strategy/framing?tab=narrative",           label: "Framing & Storytelling",            icon: Megaphone,    visibilityKey: "strategy_framing_group" },
    ]
  },
  {
    type: "section",
    label: "Fundraising",
    icon: Rocket,
    visibilityKey: null,
    children: [
      { path: "/fundraising/preparation", label: "Preparation",  icon: ClipboardList, visibilityKey: "fundraising_preparation" },
      { path: "/investors",               label: "Investors",    icon: Building2,     visibilityKey: "fundraising_find" },
      { path: "/fundraising/crm",         label: "CRM",          icon: Kanban,        visibilityKey: "fundraising_crm" },
      { path: "/fundraising/call",        label: "Call",         icon: Radio,         visibilityKey: "fundraising_call" },
    ]
  },
  {
    type: "section",
    label: "Execution",
    icon: Briefcase,
    visibilityKey: null,
    children: [
      { path: "/pharma", label: "Pharma", icon: Building, visibilityKey: "pharma" },
      { path: "/grants", label: "Grants", icon: Sparkles, visibilityKey: "grants" },
      { path: "/events", label: "Events", icon: Calendar, visibilityKey: "events" },
    ]
  },
];

export default function Sidebar({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  // All-True default across the 12 strategy sub-keys + 4 legacy group keys + 4 non-strategy keys.
  const DEFAULT_VISIBILITY = {
    // 12 sub-modules
    strategy_markets: true, strategy_value_chain: true, strategy_competitors: true,
    strategy_clinical_proof: true, strategy_regulatory_route: true, strategy_access_heor: true,
    strategy_product: true, strategy_defensibility: true, strategy_value_capture: true,
    strategy_value_inflection: true, strategy_execution_gaps: true, strategy_funding_fit: true,
    // 4 new group keys (used by the new sidebar structure)
    strategy_market_group: true, strategy_clinical_group: true, strategy_technology_group: true, strategy_milestones_group: true, strategy_framing_group: true,
    // 4 legacy group keys (kept for back-compat with older non-strategy paths)
    strategy_market: true, strategy_clinical: true, strategy_technology: true, strategy_milestones: true,
    // 4 non-strategy sections
    investors: true, pharma: true, grants: true, events: true,
    // Fundraising sub-sections (Phase 1)
    fundraising_preparation: true, fundraising_find: true, fundraising_crm: true, fundraising_call: true,
    // UX flag — hide locked modules entirely (vs show them with a lock icon)
    hide_locked_modules: false,
  };
  const [visibility, setVisibility] = useState(() => {
    // Read cached visibility synchronously on mount so the first paint already
    // reflects the correct locked state (avoids the "unlocked → locked" flash
    // on every route change, since AppLayout remounts Sidebar per route).
    try {
      const cached = typeof window !== "undefined" && localStorage.getItem("operon_sidebar_visibility");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === "object") {
          return { ...DEFAULT_VISIBILITY, ...parsed };
        }
      }
    } catch {}
    return DEFAULT_VISIBILITY;
  });
  const [expandedSections, setExpandedSections] = useState(["Strategy", "Fundraising", "Execution"]);

  useEffect(() => {
    fetchVisibility();
  }, []);
  
  const fetchVisibility = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/section-visibility`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        // Merge with defaults so any missing key falls back to True (visible).
        const merged = { ...DEFAULT_VISIBILITY, ...data };
        // Legacy migration — if a saved record only has the old 12 sub-keys, roll them up:
        // a group is OFF if ANY of its 3 legacy sub-keys is explicitly false, or if
        // the older whole-group key (without _group suffix) is false.
        const legacyMap = {
          strategy_market_group:     ["strategy_markets", "strategy_value_chain", "strategy_competitors"],
          strategy_clinical_group:   ["strategy_clinical_proof", "strategy_regulatory_route", "strategy_access_heor"],
          strategy_technology_group: ["strategy_product", "strategy_defensibility", "strategy_value_capture"],
          strategy_milestones_group: ["strategy_value_inflection", "strategy_execution_gaps", "strategy_funding_fit"],
        };
        Object.entries(legacyMap).forEach(([groupKey, subs]) => {
          if (data[groupKey] === undefined) {
            if (subs.some((s) => data[s] === false)) merged[groupKey] = false;
          }
          const oldName = groupKey.replace("_group", "");
          if (data[oldName] === false) merged[groupKey] = false;
        });
        setVisibility(merged);
        // Cache for the next mount so the sidebar can render synchronously.
        try { localStorage.setItem("operon_sidebar_visibility", JSON.stringify(merged)); } catch {}
      }
    } catch (e) {
      console.error("Failed to fetch visibility:", e);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
    } catch (e) {}
    try { localStorage.removeItem("operon_sidebar_visibility"); } catch {}
    navigate("/");
  };

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    // For strategy group entries: match the parent shell only (any tab).
    // Sidebar now has one entry per group (Market/Clinical/Tech/Milestones),
    // and the user switches sub-topic tabs inside the page.
    if (path.startsWith("/strategy/")) {
      const qIdx = path.indexOf("?");
      const basePath = qIdx === -1 ? path : path.substring(0, qIdx);
      return location.pathname === basePath;
    }
    // Non-strategy paths with a query string still match tab-scoped.
    const qIdx = path.indexOf("?");
    if (qIdx !== -1) {
      const basePath = path.substring(0, qIdx);
      if (location.pathname !== basePath) return false;
      const targetTab = path.substring(path.indexOf("tab=") + 4).split("&")[0];
      const search = location.search || "";
      const searchTabIdx = search.indexOf("tab=");
      const currentTab = searchTabIdx === -1 ? "" : search.substring(searchTabIdx + 4).split("&")[0];
      return currentTab === targetTab;
    }
    return location.pathname.startsWith(path);
  };

  const isSectionActive = (children) => {
    return children?.some(child => isActive(child.path));
  };

  const toggleSection = (label) => {
    setExpandedSections(prev => 
      prev.includes(label) 
        ? prev.filter(s => s !== label)
        : [...prev, label]
    );
  };

  const sidebarWidth = collapsed ? "w-16" : "w-64";

  const renderNavItem = (item, isMobile = false, inSection = false) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const isLocked = item.visibilityKey && !visibility[item.visibilityKey];

    if (isLocked) {
      // Hide-entirely mode: don't render the locked row at all.
      if (visibility.hide_locked_modules) return null;
      // Otherwise show locked state
      return (
        <div
          key={item.path}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-500 cursor-not-allowed ${
            collapsed && !isMobile ? "justify-center" : ""
          } ${inSection ? "pl-10" : ""}`}
          title={`${item.label} is locked`}
        >
          <Icon className="w-5 h-5 flex-shrink-0 opacity-50" />
          {(!collapsed || isMobile) && (
            <>
              <span className="font-medium flex-1 opacity-50">{item.label}</span>
              <Lock className="w-4 h-4 text-slate-500" />
            </>
          )}
        </div>
      );
    }
    
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={isMobile ? () => setMobileOpen(false) : undefined}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
          active 
            ? "bg-teal-500/20 text-teal-400" 
            : "text-slate-400 hover:bg-slate-800 hover:text-white"
        } ${collapsed && !isMobile ? "justify-center" : ""} ${inSection ? "pl-10" : ""}`}
        data-testid={`nav-${item.path.slice(1)}`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {(!collapsed || isMobile) && <span className="font-medium">{item.label}</span>}
      </Link>
    );
  };

  const renderSection = (section, isMobile = false) => {
    const Icon = section.icon;
    const isExpanded = expandedSections.includes(section.label);
    const hasActiveChild = isSectionActive(section.children);
    const isSectionLocked = section.visibilityKey && !visibility[section.visibilityKey];
    
    // Check if all children are locked
    const allChildrenLocked = section.children?.every(
      child => child.visibilityKey && !visibility[child.visibilityKey]
    );
    
    // If section itself is locked or all children are locked, show locked state
    if (isSectionLocked || (section.children?.length > 0 && allChildrenLocked)) {
      // Hide-entirely mode: skip the row completely.
      if (visibility.hide_locked_modules) return null;
      if (collapsed && !isMobile) {
        return (
          <div
            key={section.label}
            className="flex items-center justify-center px-3 py-2.5 rounded-lg text-slate-500 cursor-not-allowed"
            title={`${section.label} is locked`}
          >
            <Icon className="w-5 h-5 opacity-50" />
          </div>
        );
      }
      
      return (
        <div key={section.label} className="space-y-1">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-500 cursor-not-allowed">
            <Icon className="w-5 h-5 flex-shrink-0 opacity-50" />
            <span className="font-medium flex-1 opacity-50">{section.label}</span>
            <Lock className="w-4 h-4 text-slate-500" />
          </div>
        </div>
      );
    }
    
    // Don't render empty sections
    if (!section.children || section.children.length === 0) {
      return null;
    }
    
    if (collapsed && !isMobile) {
      // In collapsed mode, just show the icon with a tooltip
      return (
        <div key={section.label} className="space-y-1">
          <div
            className={`flex items-center justify-center px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
              hasActiveChild ? "bg-teal-500/10 text-teal-400" : "text-slate-400 hover:bg-slate-800 hover:text-white"
            }`}
            title={section.label}
          >
            <Icon className="w-5 h-5" />
          </div>
          {section.children.map(child => {
            const ChildIcon = child.icon;
            const childActive = isActive(child.path);
            const childLocked = child.visibilityKey && !visibility[child.visibilityKey];

            if (childLocked) {
              if (visibility.hide_locked_modules) return null;
              return (
                <div
                  key={child.path}
                  className="flex items-center justify-center px-3 py-2 rounded-lg text-slate-500 cursor-not-allowed"
                  title={`${child.label} is locked`}
                >
                  <ChildIcon className="w-4 h-4 opacity-50" />
                </div>
              );
            }
            
            return (
              <Link
                key={child.path}
                to={child.path}
                className={`flex items-center justify-center px-3 py-2 rounded-lg transition-all ${
                  childActive ? "bg-teal-500/20 text-teal-400" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
                title={child.label}
              >
                <ChildIcon className="w-4 h-4" />
              </Link>
            );
          })}
        </div>
      );
    }
    
    return (
      <div key={section.label} className="space-y-1">
        <button
          onClick={() => toggleSection(section.label)}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-left transition-all ${
            hasActiveChild ? "bg-teal-500/10 text-teal-400" : "text-slate-400 hover:bg-slate-800 hover:text-white"
          }`}
        >
          <Icon className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium flex-1">{section.label}</span>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
        
        {isExpanded && (
          <div className="space-y-1">
            {section.children.map(child => renderNavItem(child, isMobile, true))}
          </div>
        )}
      </div>
    );
  };

  const renderNavigation = (isMobile = false) => {
    return navStructure.map(item => {
      if (item.type === "section") {
        return renderSection(item, isMobile);
      }
      return renderNavItem(item, isMobile);
    });
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex flex-col h-screen bg-[#121318] text-white ${sidebarWidth} transition-all duration-300 relative flex-shrink-0`}>
        {/* Logo */}
        <div className={`flex items-center justify-center p-4 border-b border-slate-800`}>
          {collapsed ? (
            <img 
              src="/icons/operon-icon.png" 
              alt="Operon" 
              className="w-9 h-9 rounded-lg"
              loading="eager"
              decoding="sync"
              fetchPriority="high"
            />
          ) : (
            <img 
              src="/logo-complete.png" 
              alt="Operon - Activate the right pathways" 
              className="h-[72px] object-contain"
              loading="eager"
              decoding="sync"
              fetchPriority="high"
            />
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {renderNavigation(false)}
        </nav>

        {/* User Section */}
        <div className="border-t border-slate-800 p-3">
          {user && (
            <div className={`flex items-center gap-3 px-3 py-2 mb-2 ${collapsed ? "justify-center" : ""}`}>
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-sm font-medium">{user.name?.charAt(0)}</span>
                )}
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user.email}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Admin Link */}
          {user?.email === "marcodelise0@gmail.com" && (
            <Link
              to="/admin"
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-all mb-1 ${collapsed ? "justify-center" : ""}`}
              data-testid="nav-admin"
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm">Admin Panel</span>}
            </Link>
          )}
          
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-red-400 transition-all w-full ${collapsed ? "justify-center" : ""}`}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm">Sign Out</span>}
          </button>
        </div>

        {/* Collapse Button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
          data-testid="collapse-sidebar"
        >
          <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
        </button>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-[#121318] border-b border-slate-800 flex items-center justify-between px-4 z-50">
        <img 
          src="/logo-complete.png" 
          alt="Operon" 
          className="h-14 object-contain"
        />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(true)}
          className="text-white hover:bg-slate-800"
        >
          <Menu className="w-5 h-5" />
        </Button>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 bg-[#121318] text-white flex flex-col">
            {/* Logo */}
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <img 
                src="/logo-complete.png" 
                alt="Operon" 
                className="h-16 object-contain"
              />
              <button onClick={() => setMobileOpen(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {renderNavigation(true)}
            </nav>

            {/* User Section */}
            <div className="border-t border-slate-800 p-3">
              {user && (
                <div className="flex items-center gap-3 px-3 py-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm font-medium">{user.name?.charAt(0)}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{user.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                </div>
              )}
              
              {user?.email === "marcodelise0@gmail.com" && (
                <Link
                  to="/admin"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-all mb-1"
                >
                  <Settings className="w-5 h-5" />
                  <span className="text-sm">Admin Panel</span>
                </Link>
              )}
              
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-red-400 transition-all w-full"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
